import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LiliButton } from '@lili/lili-frontend-common';
import Button from '../../../../generic-components/Button';
import { POPUP_CLOSE_BTN_TEXT } from '../../constants';
import Icon from '../../../../generic-components/Icon';

interface IProps {
  pictureSrc: string;
  pictureClassName?: string;
  picture2ClassName?: string;
  picture3ClassName?: string;
  picture2Src?: string;
  picture3Src?: string;
  title: string;
  text: string;
  text2?: string;
  showInfoPopup: boolean;
  handleState: () => void;
  secondaryHandleState?: () => void;
  mainButtonText?: string;
  secondaryButtonText?: string;
  additionalCallback?: () => void;
  handleManualLoading?: () => void;
  popupHeight?: string;
  titleClassName?: string;
  textClassName?: string;
  text2ClassName?: string;
  isLoading?: boolean;
  buttonClassName?: string;
  className?: string;
  flexParams?: string;
  secondaryButtonClassName?: string;
  multiFormatTexts?: React.ReactElement<any, any>;
  mainButtonClassName?: string;
  needIconX?: boolean;
  xIconSrc?: string;
  xButtonClassName?: string;
  overrideContainerClassName?: boolean;
}

const InfoPopup = ({
  pictureSrc,
  title,
  text,
  handleState,
  showInfoPopup,
  className,
  mainButtonText,
  secondaryButtonText,
  secondaryHandleState,
  additionalCallback,
  popupHeight,
  pictureClassName,
  titleClassName,
  textClassName,
  isLoading,
  buttonClassName,
  flexParams,
  secondaryButtonClassName,
  text2,
  text2ClassName,
  multiFormatTexts,
  picture2Src,
  picture2ClassName,
  picture3Src,
  picture3ClassName,
  needIconX,
  xIconSrc,
  xButtonClassName,
  overrideContainerClassName,
}: IProps) => {
  const infoPopupRef = useRef<HTMLDivElement>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(isLoading !== undefined ? isLoading : false);

  const handleClickOutside = (event: Event) => {
    if (infoPopupRef.current && event.target && infoPopupRef.current === event.target) {
      if (secondaryHandleState) {
        secondaryHandleState();
      } else handleState();
    }
  };

  useEffect(() => {
    setIsLoadingData(isLoading !== undefined ? isLoading : false);
  }, [isLoading]);

  useEffect(() => {
    if (showInfoPopup) document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLoadingData]);

  return createPortal(
    <div ref={infoPopupRef} className='bg-bw07 fixed top-0 left-0 h-screen w-screen bg-opacity-70 z-50'>
      <div
        className={`${overrideContainerClassName ? '' : 'w-[400px] h-[504px]'} ${popupHeight} px-[24px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-bw02 rounded-lg ${flexParams ?? 'flex flex-col justify-center items-center'} ${className} z-50`}
      >
        {needIconX && xIconSrc && (
          <Button
            onClick={() => {
              if (additionalCallback) {
                additionalCallback();
              } else handleState();
            }}
            childElement={<Icon src={xIconSrc} />}
            className={xButtonClassName ?? ''}
            id='closeIcon'
          />
        )}
        <img
          className={pictureClassName ?? 'max-w-[5.25rem] max-h-[5.25rem] my-[2.313rem]'}
          src={pictureSrc}
          alt='icon1'
        />
        {picture2Src && (
          <img
            className={picture2ClassName ?? 'max-w-[5.25rem] max-h-[5.25rem] my-[3.313rem]'}
            src={picture2Src}
            alt='icon2'
          />
        )}
        {picture3Src && (
          <img
            className={picture3ClassName ?? 'max-w-[5.25rem] max-h-[5.25rem] my-[3.313rem]'}
            src={picture3Src}
            alt='icon3'
          />
        )}
        <span id='popupTitle' className={titleClassName || 'text-base font-metricMedium text-bw08 mb-[1rem]'}>
          {title}
        </span>
        {multiFormatTexts ?? (
          <span
            id='popupText'
            className={`text-base text-center font-metric text-bw07 ${textClassName ?? 'mb-[5.438rem]'}`}
          >
            {text}
          </span>
        )}
        {text2 && (
          <span id='popupText2' className={text2ClassName ?? 'text-base text-center font-metric text-bw07'}>
            {text2}
          </span>
        )}
        <LiliButton
          id='InfoPopupShowOrHide'
          onClick={() => {
            if (additionalCallback) {
              additionalCallback();
            } else handleState();
          }}
          isLoading={isLoading}
          className={`${buttonClassName ?? 'w-[10rem] h-[3rem]'}`}
          title={mainButtonText ?? POPUP_CLOSE_BTN_TEXT}
        />
        {secondaryHandleState && secondaryButtonText && (
          <Button
            title={secondaryButtonText}
            className={secondaryButtonClassName ?? 'mt-4 mb-8 text-sm text-bw06 font-metricMedium hover:text-action03'}
            id='secondaryButton'
            onClick={() => {
              secondaryHandleState();
            }}
          />
        )}
      </div>
    </div>,
    document.getElementById('portal')!,
  );
};

export default InfoPopup;
